import React, { useEffect, useState } from "react";

import {
    ActivityIndicator,
    Platform,
    Text,
    View
} from "react-native";
import Style from "../../utils/Style";
import AppTheme from "../../utils/Theme";
import SVGIconButton from "../../designSystem/Buttons/SVGIconButton";
import { IcClose } from "../../utils/Icons";
import { getLocalDocs } from "../../../specific/utils/LocalStorage";
import config from "../../../../config";
import { getLanguage } from "../../utils/Localization/Localization";
import LiliWebView from "../../../specific/components/Webview/LiliWebView";
import PrintButton from "../../../specific/views/PrintButton";
import Docs from "../../models/Docs";
import HTMLDisplay from "../../../specific/components/Webview/HTMLDisplay";
import User from "../../data/user/User";
import { AppTarget, getAppTarget } from "../../../specific/utils/Navigation/Host";
import { AsyncStorageKey, getItemFromAsyncStorage } from "../../utils/AsyncStorage/AsyncStorage";
import NavBarButton from "../NavBar/NavBarButton";
import SafeAreaView from "../../../specific/views/SafeAreaView";
import DocumentContainer from "./DocumentContainer";

type translatedString = {
    [l: string]: string;
}

type translatedImage = {
    [l: string]: { url: string, id: string };
}

export type PDFDocument = {
    id?: string;
    created_at?: string;
    last_update?: string;
    title: translatedString;
    color: string;
    header_center_title: translatedString;
    header_left_title: translatedString;
    header_right_title: translatedString;
    header_left_icon: translatedImage;
    header_right_icon: translatedImage;
    body: any
};

interface DocumentsViewerProps {
    documentId: string;
    onDocumentCloseRequest: () => void;
}

const DocumentsViewer = (props: DocumentsViewerProps) => {

    const { documentId, onDocumentCloseRequest } = props;
    const appTheme: AppTheme = new AppTheme();
    const user: User = User.getInstance();

    const [viewDidAppear, setViewDidAppear] = useState<boolean>(false);
    const [document, setDocument] = useState<PDFDocument | undefined>(undefined);
    const [mainColor, setMainColor] = useState(appTheme.darkBlue);
    const [editionModeValue, setEditionModeValue] = useState<boolean>(false);

    useEffect(() => {
        if (document !== undefined) {
            const { color } = document;
            if (color !== undefined) {
                setMainColor(color);
            }
            setViewDidAppear(true);
        } else {
            onViewWillAppear();
        }
    }, [document]);

    const getEditionValue = async () => {
        if (user.admin === true) {
            try {
                const adminDataString = await getItemFromAsyncStorage({ itemKey: AsyncStorageKey.adminData });
                if (adminDataString === null) {
                    setEditionModeValue(false);
                    return false;
                } else {
                    const adminData = JSON.parse(adminDataString);
                    if (adminData.editionMode !== undefined) {
                        setEditionModeValue(adminData.editionMode);
                        return adminData.editionMode;
                    } else {
                        setEditionModeValue(false);
                        return false;
                    }
                }
            } catch (error) {
                setEditionModeValue(false);
                return false;
            }
        }
        return false;
    }

    const onViewWillAppear = async () => {
        // On va récupérer les donnnées admin si l'utilisateur est admin
        let isEditionModeEnabled: boolean = await getEditionValue();
        let localDocs = undefined;
        if (Platform.OS === "web") {
            const docsInstance: Docs = Docs.getInstance();
            localDocs = docsInstance;
            if (Object.keys(localDocs.data).length === 0) {
                localDocs = await getLocalDocs();
            }
        } else {
            localDocs = await getLocalDocs();
        }
        const appTarget: AppTarget = getAppTarget();
        if (appTarget !== "public") {
            isEditionModeEnabled = false;
        }
        if ((localDocs !== undefined) && (localDocs.data !== undefined) && (localDocs.data[documentId] !== undefined)) {
            if ((config.appLabel === "prod") || (isEditionModeEnabled === false)) {
                // On regarde si on a un doucment "published"
                if (localDocs.data[documentId].published !== undefined) {
                    setDocument(localDocs.data[documentId].published);
                } else {
                    onDocumentCloseRequest();
                }
            } else {
                // On regarde si on a un document "draft"
                if (localDocs.data[documentId].draft !== undefined) {
                    setDocument(localDocs.data[documentId].draft)
                } else {
                    onDocumentCloseRequest();
                }
            }
        } else {
            onDocumentCloseRequest();
        }
    }

    const renderWebPage = () => {
        const { title } = document;
        let htmlToDispaly: string = document[getLanguage()];
        if (Platform.OS === "web") {
            return <HTMLDisplay html={htmlToDispaly} />
        } else {
            htmlToDispaly = htmlToDispaly.replace('rel="stylesheet"</head>', 'rel="stylesheet" /></head>');
            htmlToDispaly = htmlToDispaly.replace('</head>', '<style>.sheet-outer.A4 .sheet { width:auto; max-width: 210mm; display: flex; flex-direction: column; align-items: center;}</style></head>');
            return <LiliWebView hideNavbar={true} html={htmlToDispaly} title={title} color={mainColor} />;
        }
    }

    const renderDocumentContent = () => {
        if (viewDidAppear === false) {
            return <View style={{ width: appTheme.getFullScreenWidth(), flex: 1, justifyContent: "center", alignItems: "center", backgroundColor: "#FFFFFF" }}>
                <ActivityIndicator />
            </View>
        }
        const { title, pdfs } = document;
        let pdfToPrint: string | undefined = undefined;
        if ((pdfs !== undefined) && (pdfs !== null)) {
            if ((pdfs[getLanguage()] !== undefined) && (pdfs[getLanguage()].length > 0)) {
                pdfToPrint = pdfs[getLanguage()];
            }
        }
        return <DocumentContainer
            documentTitle={title[getLanguage()]}
            htmlToPrint={document[getLanguage()]}
            pdfToPrint={pdfToPrint}
            isDraft={editionModeValue}
            mainColor={mainColor}
            onDocumentCloseRequest={onDocumentCloseRequest}>
            {renderWebPage()}
        </DocumentContainer>
    }

    return renderDocumentContent()
}

export default DocumentsViewer;